import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';

const TAXIDERMY_LOGO = '/taxidermy/logo.png';

type GalleryCategory = 'all' | 'bird' | 'mammal' | 'restoration' | 'other';

const categoryLabels: Record<GalleryCategory, string> = {
  all: 'All',
  bird: 'Birds',
  mammal: 'Mammals',
  restoration: 'Restoration',
  other: 'Other',
};

interface GalleryItem {
  id: string;
  title: string;
  description: string | null;
  category: string;
  image_url: string | null;
  thumbnail_url: string | null;
  price: number | null;
  price_display: string;
  status: string;
  tags: string[];
  species: string | null;
  dimensions: string | null;
  featured: boolean;
}

/**
 * Gallery page for taxidermy work
 */
export function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState<GalleryCategory>('all');
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch gallery items from API
  useEffect(() => {
    async function fetchItems() {
      try {
        const response = await fetch('/api/gallery');
        const data = await response.json();
        setItems(data.items || []);
      } catch (error) {
        console.error('Failed to fetch gallery items:', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchItems();
  }, []);

  const filteredItems =
    selectedCategory === 'all'
      ? items
      : items.filter((item) => item.category === selectedCategory);

  return (
    <>
      {/* Page Header */}
      <section className="section-padding relative overflow-hidden bg-gradient-to-b from-primary/5 to-transparent">
        {/* Background Logo - Engraved Effect */}
        <div
          className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 lg:right-[10%]"
          aria-hidden="true"
        >
          <div className="relative">
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="h-[350px] w-[350px] object-contain opacity-[0.08] blur-[1px] dark:invert lg:h-[450px] lg:w-[450px]"
            />
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="absolute inset-0 h-[350px] w-[350px] translate-x-[2px] translate-y-[2px] object-contain opacity-[0.03] dark:invert lg:h-[450px] lg:w-[450px]"
            />
          </div>
        </div>

        <div className="container-custom relative">
          <div className="max-w-2xl">
            <span className="category-label">Taxidermy Showcase</span>
            <h1 className="text-4xl font-bold md:text-5xl">Gallery</h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Browse my collection of taxidermy work. Each piece is crafted with
              precision and care to preserve the natural beauty of the specimen.
            </p>
            <div className="mt-8">
              <Button asChild className="gap-2">
                <Link to="/contact">
                  Enquire About a Piece
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="border-b border-border bg-card py-4">
        <div className="container-custom">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="mr-2 text-sm font-medium">Filter:</span>
            {(Object.keys(categoryLabels) as GalleryCategory[]).map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                aria-pressed={selectedCategory === category}
              >
                {categoryLabels[category]}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding" aria-labelledby="gallery-grid">
        <div className="container-custom">
          <h2 id="gallery-grid" className="sr-only">
            Gallery Items
          </h2>

          {isLoading ? (
            // Loading state
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            // Empty state
            <Card className="relative overflow-hidden border-dashed">
              {/* Subtle Background Logo */}
              <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03]"
                aria-hidden="true"
              >
                <img
                  src={TAXIDERMY_LOGO}
                  alt=""
                  className="h-64 w-64 object-contain dark:invert"
                />
              </div>

              <CardContent className="relative flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4">
                  <img
                    src={TAXIDERMY_LOGO}
                    alt=""
                    className="h-20 w-20 object-contain opacity-50 dark:invert"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="text-xl font-semibold">Gallery Coming Soon</h3>
                <p className="mt-2 max-w-md text-muted-foreground">
                  I'm currently preparing my gallery. In the meantime, feel free to
                  contact me to discuss my taxidermy work or to request photos of
                  specific pieces.
                </p>
                <Button asChild className="mt-6 gap-2">
                  <Link to="/contact">
                    Contact Me
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            // Gallery grid
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredItems.map((item) => (
                <Card key={item.id} className="group card-hover overflow-hidden">
                  <div className="relative aspect-square overflow-hidden bg-muted">
                    {(item.thumbnail_url || item.image_url) ? (
                      <img
                        src={item.thumbnail_url || item.image_url || ''}
                        alt={item.description || item.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <img
                          src={TAXIDERMY_LOGO}
                          alt=""
                          className="h-16 w-16 object-contain opacity-20 dark:invert"
                        />
                      </div>
                    )}
                    {/* Status badge */}
                    {item.status === 'sold' && (
                      <div className="absolute left-2 top-2 rounded bg-amber-500 px-2 py-1 text-xs font-semibold text-white">
                        Sold
                      </div>
                    )}
                    {item.status === 'reserved' && (
                      <div className="absolute left-2 top-2 rounded bg-blue-500 px-2 py-1 text-xs font-semibold text-white">
                        Reserved
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                          {categoryLabels[item.category as GalleryCategory] || item.category}
                        </span>
                        <h3 className="mt-1 font-semibold text-white">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {categoryLabels[item.category as GalleryCategory] || item.category}
                    </span>
                    <h3 className="mt-1 font-semibold">{item.title}</h3>
                    {item.species && (
                      <p className="text-sm text-muted-foreground">{item.species}</p>
                    )}
                    {item.description && (
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {item.description}
                      </p>
                    )}
                    {/* Price display */}
                    <div className="mt-2 text-sm">
                      {item.price_display === 'fixed' && item.price ? (
                        <span className="font-semibold text-primary">${item.price}</span>
                      ) : item.price_display === 'sold' ? (
                        <span className="text-amber-500">Sold</span>
                      ) : item.price_display === 'not_for_sale' ? (
                        <span className="text-muted-foreground">Not for sale</span>
                      ) : (
                        <span className="text-muted-foreground">Price on request</span>
                      )}
                    </div>
                    <Button asChild variant="ghost" size="sm" className="mt-3 -ml-3">
                      <Link to="/contact">
                        Enquire
                        <ArrowRight className="ml-1 h-3 w-3" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
